import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { InstructionUserInputComponent } from "../instruction-user-input/instruction-user-input.component";
import { InstructionUserFileUploadComponent } from "../instruction-user-file-upload/instruction-user-file-upload.component";
import { AutomationExecutionService } from "./automation-execution.service";
import { AutomationStateService } from "./automation-state.service";
import { PromptLibraryService } from "./prompt-library.service";
import { ChatDocumentService } from "./chat-document.service";
import { AiChatService } from './ai-chat.service';
import { AiChatStreamingService } from './ai-chat-streaming.service';
import { MatBottomSheet, MatBottomSheetConfig } from '@angular/material/bottom-sheet';
import { InstructionType } from '../../../enums/automation.enum';
import { IInstructions } from '../../automation/interfaces/i-instructions';
import { PromptLibraryListItemSummaryComponent } from '../prompt-library/prompt-library-list/prompt-library-list-item-summary/prompt-library-list-item-summary.component';
import { AgentService } from '../../agent/services/agent.service';
import { SpinnerService } from "../../../shared/spinner/spinner.service";
import { ChatSessionService } from './chat-session.service';
import { ConversationStarterService } from './conversation-starter.service';
import { MultiSessionService } from '../../../services/multi-session.service';

@Injectable({
  providedIn: 'root'
})
export class AutomationUtilitiesService {
  readonly dialog = inject(MatDialog);
  // Track which sessions have dialogs open to support multiple concurrent sessions
  #openDialogSessions = new Set<string | number>();
  #automationExecutionService = inject(AutomationExecutionService);
  #automationStateService = inject(AutomationStateService);
  #promptLibraryService = inject(PromptLibraryService);
  #aiChatService = inject(AiChatService);
  #aiChatStreamingService = inject(AiChatStreamingService);
  #chatDocumentService = inject(ChatDocumentService);
  #agentService = inject(AgentService);
  #bottomSheet = inject(MatBottomSheet);
  #spinnerService = inject(SpinnerService);
  #chatSessionService = inject(ChatSessionService);
  #conversationStarterService = inject(ConversationStarterService);
  #multiSessionService = inject(MultiSessionService);

  /**
   * Opens the user input dialog for a session.
   * If the user navigates away while waiting for input, the session is paused.
   * When navigating back, this method can be called to resume the session.
   */
  openInstructionUserInputDialog(isWf = true, sessionId?: string | number) {
    const currentSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    
    // Prevents opening multiple dialogs for the same session
    if (this.#openDialogSessions.has(currentSessionId)) {
      return;
    } else {
      this.#openDialogSessions.add(currentSessionId);
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.width = '55vw';
      dialogConfig.maxWidth = '55vw';
      this.#spinnerService.hide();

      const sessionState = this.#automationStateService.getSessionState(currentSessionId);
      const workflowId = sessionState?.workflowExecutionState?.workflowExecutionId;
      const instruction = this.#multiSessionService.getSessionInstructionInProgress(currentSessionId)
        ?? sessionState?.instructionInProgress
        ?? this.#automationStateService.instructionInProgress(); // Fallback to global signal for standalone instructions

      if (!instruction) {
        throw new Error(`openInstructionUserInputDialog: no instruction found for session ${currentSessionId}`);
      }
      
      // Update session-specific state with the instruction if not already set
      if (!sessionState?.instructionInProgress) {
        this.#automationStateService.updateSessionState(currentSessionId, {
          instructionInProgress: instruction
        });
      }
      
      // BUG FIX: Only pause the session if it's not already waiting for input.
      // 
      // ISSUE: Dialog was appearing twice during workflow execution because pauseSessionForUserInput
      // was being called in two places:
      //   1. AutomationExecutionService.#handleNextInstruction() when reaching a CollectInfo instruction
      //   2. Here in openInstructionUserInputDialog() when the effect triggers the dialog to open
      // 
      // Each call triggered state updates that caused Angular's change detection to fire multiple times,
      // resulting in duplicate dialogs and incorrect prompt handling.
      // 
      // SOLUTION: Check if session is already waiting for input before pausing again.
      // - In workflow execution (isWf=true): Session is already paused by AutomationExecutionService, skip.
      // - In standalone execution (isWf=false) or navigation back to paused session: Needs pausing here.
      if (!this.#multiSessionService.isSessionWaitingForInput(currentSessionId)) {
        this.#multiSessionService.pauseSessionForUserInput(
          currentSessionId,
          InstructionType.CollectInformation,
          workflowId,
          instruction.id,
          instruction
        );
      }

      dialogConfig.data = { instruction };
      const dialogRef = this.dialog.open(InstructionUserInputComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((response: string) => {
        this.#openDialogSessions.delete(currentSessionId);
        
        // Clear user input state for this session in MultiSessionService
        this.#multiSessionService.clearUserInputState(currentSessionId);
        
        // Update session-specific AutomationStateService flags
        this.#automationStateService.updateSessionState(currentSessionId, {
          isWaitingForUserInput: false
        });
        
        if (response) {
          // Resume the session
          this.#multiSessionService.resumeSessionFromUserInput(currentSessionId);
          
          if (isWf) {
            this.#automationExecutionService.continueInstructionExecutionWithUserInput(response, currentSessionId);
          } else {
            const sessionState = this.#automationStateService.getSessionState(currentSessionId);
            const instructionPrompt = sessionState?.instructionInProgress;
            if (!instructionPrompt) {
              console.error('No instruction prompt found for user input in session:', currentSessionId);
              return;
            }
            const prompt = `${instructionPrompt.details?.prompt || ''} ${response}`;
            this.#promptLibraryService.setSelectedPromptForCurrentSession(prompt, instructionPrompt);
            this.#aiChatStreamingService.sendMessageWithStreaming(prompt, undefined, currentSessionId);
          }
        } else {
          // User canceled - check if workflow is in progress
          const sessionState = this.#automationStateService.getSessionState(currentSessionId);
          const isWorkflowInProgress = sessionState?.isWorkflowInProgress;
          
          if (isWorkflowInProgress) {
            // Cancel the entire workflow
            this.#automationStateService.updateSessionState(currentSessionId, {
              isWorkflowInProgress: false,
              workflowExecutionState: null,
              instructionInProgress: null
            });
          }
          
          // Revert session icon to DEFAULT since user canceled
          this.#multiSessionService.completeSession(currentSessionId);
        }
      });
    }
  }

  /**
   * Opens the file upload dialog for a session.
   * If the user navigates away while waiting for file upload, the session is paused.
   * When navigating back, this method can be called to resume the session.
   */
  async openInstructionUserFileUploadDialog(isWf = true, sendMessage = false, sessionId?: string | number) {
    const currentSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();

    // Prevents opening multiple dialogs for the same session
    if (this.#openDialogSessions.has(currentSessionId)) {
      return;
    } else {
      this.#openDialogSessions.add(currentSessionId);
      
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.width = '55vw';
    dialogConfig.maxWidth = '55vw';
    dialogConfig.autoFocus = 'fileUploadDialog';
    dialogConfig.data = { sessionId: currentSessionId };
    this.#spinnerService.hide();

    const sessionState = this.#automationStateService.getSessionState(currentSessionId);
    const workflowId = sessionState?.workflowExecutionState?.workflowExecutionId;
    const instruction = this.#multiSessionService.getSessionInstructionInProgress(currentSessionId)
      ?? sessionState?.instructionInProgress
      ?? this.#automationStateService.instructionInProgress(); // Fallback to global signal for standalone instructions

    if (!instruction) {
      throw new Error(`openInstructionUserFileUploadDialog: no instruction found for session ${currentSessionId}`);
    }
    
    // Update session-specific state with the instruction if not already set
    if (!sessionState?.instructionInProgress) {
      this.#automationStateService.updateSessionState(currentSessionId, {
        instructionInProgress: instruction
      });
    }
    
    // BUG FIX: Only pause the session if it's not already waiting for input.
    // 
    // ISSUE: Dialog was appearing twice during workflow execution because pauseSessionForUserInput
    // was being called in two places:
    //   1. AutomationExecutionService.#handleNextInstruction() when reaching a FileUpload instruction
    //   2. Here in openInstructionUserFileUploadDialog() when the effect triggers the dialog to open
    // 
    // Each call triggered state updates that caused Angular's change detection to fire multiple times,
    // resulting in duplicate dialogs and incorrect file handling.
    // 
    // SOLUTION: Check if session is already waiting for input before pausing again.
    // - In workflow execution (isWf=true): Session is already paused by AutomationExecutionService, skip.
    // - In standalone execution (isWf=false) or navigation back to paused session: Needs pausing here.
    if (!this.#multiSessionService.isSessionWaitingForInput(currentSessionId)) {
      this.#multiSessionService.pauseSessionForUserInput(
        currentSessionId,
        InstructionType.FileUpload,
        workflowId,
        instruction.id,
        instruction
      );
    }

    dialogConfig.data = { instruction };

    const dialogRef = this.dialog.open(InstructionUserFileUploadComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(async (response: string) => {
      this.#openDialogSessions.delete(currentSessionId);
      
      // Clear user input state for this session in MultiSessionService
      this.#multiSessionService.clearUserInputState(currentSessionId);
      
      // Update session-specific AutomationStateService flags
      this.#automationStateService.updateSessionState(currentSessionId, {
        isWaitingForUserInput: false,
        isWaitingForFileUpload: false,
        isFileUploadInProgress: false
      });
      
      // Set flag for setting the SL by attached documents to false
      this.#conversationStarterService.setSensitivityLabelByAttachedDocuments.set(false);

      if (response === 'success') {
        // Files were attached and user clicked Continue
        this.#automationStateService.updateSessionState(currentSessionId, {
          isFileUploadInProgress: true
        });

        // Resume the session
        this.#multiSessionService.resumeSessionFromUserInput(currentSessionId);

        if (isWf) {
          // First update the chat session (skip refresh from backend to preserve local message state)
          await this.#automationExecutionService.updateChatSession(currentSessionId, true);

          // Then send the message with file references
          const sessionState = this.#automationStateService.getSessionState(currentSessionId);
          const instruction = sessionState?.instructionInProgress;
          this.#aiChatService.sendMessage(instruction?.details.prompt!, undefined, currentSessionId).then((isSubmitting: boolean | undefined) => {
            if (!isSubmitting) {
              this.#automationStateService.updateSessionState(currentSessionId, {
                isFileUploadInProgress: false
              });
              this.#automationExecutionService.continueInstructionExecutionWithFileAttachments(currentSessionId);
            }
          });
        } else {
          // For direct instruction, set just the instruction prompt without document references
          // The documents are already tracked by chatDocumentService.selectedDocuments()
          const sessionState = this.#automationStateService.getSessionState(currentSessionId);
          const instructionPrompt = sessionState?.instructionInProgress;
          if (!instructionPrompt) {
            console.error('No instruction prompt found for file upload in session:', currentSessionId);
            return;
          }
          // Set only the instruction prompt text without adding file references
          // The actual files are already available through chatDocumentService.selectedDocuments()
          const prompt = instructionPrompt.details?.prompt || '';

          if (sendMessage) {
            // This is for file upload instructions that are called from the start session page.  It will automatically
            // send the message after attaching the document(s).
            await this.#aiChatStreamingService.sendMessageWithStreaming(prompt, undefined, currentSessionId);
          } else {
            this.#promptLibraryService.setSelectedPromptForCurrentSession(prompt, instructionPrompt);
          }
        }
      } else {
        // User canceled or closed the dialog
        // Clear any selected documents for upload
        this.#chatDocumentService.selectedDocuments.set([]);
        this.#chatSessionService.setSessionInputData(currentSessionId, {
          text: '',
          documents: []
        });
        
        // Check if workflow is in progress
        const sessionState = this.#automationStateService.getSessionState(currentSessionId);
        const isWorkflowInProgress = sessionState?.isWorkflowInProgress;
        
        if (isWorkflowInProgress) {
          // Cancel the entire workflow
          this.#automationStateService.updateSessionState(currentSessionId, {
            isWorkflowInProgress: false,
            workflowExecutionState: null,
            instructionInProgress: null
          });
        }
        
        // Revert session icon to DEFAULT since user canceled
        this.#multiSessionService.completeSession(currentSessionId);
      }
    });
    }
  }

    /**
   * Handles displaying instruction details in a bottom sheet
   * @param instructionPrompt The instruction to display
   */
  handleInstructionPrompt(instructionPrompt: IInstructions): void {
    // Get the model string
    const model = instructionPrompt.details?.model ?
      `${instructionPrompt.details.model.accountName}/${instructionPrompt.details.model.model}` : '';

    // Resolve agent name
    const agentName = instructionPrompt.details?.agent ?
      this.#agentService.getAgentNameFromId(instructionPrompt.details.agent) : undefined;

    // Create the base data object
    const data = {
      meta: {
        name: instructionPrompt.details?.name,
        description: instructionPrompt.details?.description,
        keywords: instructionPrompt.details?.keywords || '',
        topP: instructionPrompt.details?.topP,
        temperature: instructionPrompt.details?.temperature,
        model,
        prompt: instructionPrompt.details?.prompt,
        type: instructionPrompt.details?.type,
        agent: agentName,
        fileUploadLabel: instructionPrompt.details?.fileUploadLabel,
        collectInfoLabel: instructionPrompt.details?.collectInformationLabel,
        sensitivityLabels: instructionPrompt.details?.sensitivityLabels,
        users: instructionPrompt.details?.users,
        owners: instructionPrompt.details?.owners || [],
        groups: instructionPrompt.details?.groups || [],
        isUniversal: instructionPrompt.details?.isUniversal,
        isEnabled: instructionPrompt.details?.isEnabled,
      }
    };

    // Handle specific instruction types
    const type = instructionPrompt.details?.type;
    if (type === InstructionType.CollectInformation) {
      data.meta.collectInfoLabel = instructionPrompt.details?.collectInformationLabel;
    }

    this.openBottomSheetWithData(data);
  }

  /**
   * Opens the bottom sheet with the provided data
   * @param data The data to display in the bottom sheet
   */
  openBottomSheetWithData(data: any): void {
    const config: MatBottomSheetConfig = {
      hasBackdrop: true,
      autoFocus: "first-heading",
      data: data
    };

    this.#bottomSheet.open(PromptLibraryListItemSummaryComponent, config);
  }
}
